import React, { useEffect, useState } from 'react';
import { arrayOf, func, objectOf, shape, string } from 'prop-types';
import { isEqual } from 'lodash';
import { reportToSegment, types } from '@smartcar/morse';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box, Button, Divider, Tab, Tabs, useTheme } from '@mui/material';

import applicationsProps from '../../../../types/applications';
import { Chip, Credentials, PermissionGate, TabPanel } from '../../../../components';
import {
  AuthenticationSettings,
  DeleteApplication,
  ManagementToken,
  RedirectUriList,
  VehicleAccess,
} from './components';
import staticText from '../../../../localization/Application/Configuration/configuration';
import ApplicationHeader from '../ApplicationHeader';
import { getOrgApps } from '../../utils';
import { getInitialVehicleAccessSelections } from './components/VehicleAccess/util';
import { TabsNav } from '../../../../global-styles/components';

const getCombinedVehicleAccessSelections = (selectedVehicleAccess) => {
  return Object.keys(selectedVehicleAccess).reduce(
    (allSelections, vehicleAccessType) => {
      const combinedSelections =
        allSelections.concat(selectedVehicleAccess[vehicleAccessType]);

      return combinedSelections;
    }, [],
  );
};

const Configuration = ({
  actions: {
    deleteApplication,
    fetchConnectConfigRequest,
    hideSecret,
    hideManagementToken,
    initializeSelectedVehicleAccess,
    regenerateManagementToken,
    updateApplication,
    updateConnectConfigRequest,
  },
  applications,
  connectConfig,
  isFetchingConnectConfig,
  isUpdating,
  match,
  managementToken,
  selectedOrganization,
  selectedVehicleAccess,
  signals,
}) => {
  const { applicationId } = match.params;
  const application = applications[applicationId];
  const {
    name,
    redirectUris,
  } = application;
  const { webhooksV2Enabled, vehicleAccessTabEnabled } = useFlags();
  const theme = useTheme();

  const [currentTab, setCurrentTab] = useState(0);
  useEffect(() => {
    reportToSegment(types.PAGE, 'Configuration');

    fetchConnectConfigRequest();

    return (() => {
      hideSecret();
      hideManagementToken();
    });
  }, []);

  const tabToResetActionMap = {
    2: () => {
      const initialVehicleAccessSelections = getInitialVehicleAccessSelections(
        connectConfig.vehicleAccess || [],
        signals,
      );
      initializeSelectedVehicleAccess(initialVehicleAccessSelections);
    },
  };
  const tabToPublishActionMap = {
    2: () => {
      updateConnectConfigRequest({
        vehicleAccess: getCombinedVehicleAccessSelections(selectedVehicleAccess),
      });
    },
  };
  const tabToIsPublishedStatusMap = {
    2: () => {
      const vehicleAccessSelections = getCombinedVehicleAccessSelections(selectedVehicleAccess);
      return isEqual(new Set(vehicleAccessSelections), new Set(connectConfig.vehicleAccess));
    },
  };


  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleUpdateUris = (uris) => {
    updateApplication(applicationId, { redirectUris: uris });
  };
  const orgApps = getOrgApps(applications, selectedOrganization);
  // prevent deleting the org's last app,
  // otherwise we'd automatically generate a new app in the initialization step
  const canDeleteApplication = Object.keys(orgApps).length > 1;

  // Extract / create mapping once when we add more tabs
  const tabs = [
    'API keys',
    'Application details',
  ];

  if (webhooksV2Enabled && vehicleAccessTabEnabled) {
    tabs.push('Vehicle access');
  }

  return (
    <Box
      sx={{
        // maxHeight: `calc(100vh - ${theme.height.topbar} - ${theme.spacing(1)} - 50px)`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div style={{ display: 'flex', height: '100%', width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            '& .styled-scrollbar': {
              ...theme.styledScrollbar,
            },
          }}
        >
          <ApplicationHeader heading={staticText.heading} />
          <TabsNav color="transparent" component="nav" elevation={0}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              {tabs.map((tab, index) => (
                <Tab key={tab} value={index} label={tab} />
              ))}
            </Tabs>
          </TabsNav>
          <TabPanel value={currentTab} index={0} name="api-keys">
            <Box display="flex" flexDirection="column" gap={3}>
              <Credentials />
              <RedirectUriList
                updateUris={handleUpdateUris}
                redirectUris={redirectUris}
                clientId={applicationId}
                isUpdating={isUpdating}
                defaultRedirectUri={connectConfig.defaultRedirectUri}
                setDefaultRedirectUri={uri => updateConnectConfigRequest({
                  defaultRedirectUri: uri,
                })}
                isFetchingDefaultRedirectUri={isFetchingConnectConfig}
              />
              <ManagementToken
                applicationName={name}
                managementToken={managementToken}
                regenerateManagementToken={regenerateManagementToken}
              />
            </Box>
            {canDeleteApplication && (
              <PermissionGate dashboardPermission="delete_application">
                <DeleteApplication
                  applicationId={applicationId}
                  applicationName={name}
                  deleteApplication={deleteApplication}
                />
              </PermissionGate>
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={1} name="application-details">
            <AuthenticationSettings
              application={application}
              updateApplication={updateApplication}
            />
          </TabPanel>
          {webhooksV2Enabled && vehicleAccessTabEnabled && (
            <TabPanel
              value={currentTab}
              index={2}
              name="vehicle-access"
              style={{
                // Setting a max height on this div so that the scroll bar appears on the tab panel
                // contents, instead of the entire page
                // The pixel values below are subtracting the heights of everything surrounding the
                // tab panel, so that it fits in the available space
                maxHeight: 'calc(100vh - 64px - 48px - 2px - 8px - 48.5px - 47px - 16px - 13.5px - 26px - 2.5px)',

                overflowY: 'auto',
              }}
              className="styled-scrollbar"
            >
              <VehicleAccess />
            </TabPanel>
          )}
        </Box>
        {/* <div style={{ width: '50%' }}>Preview</div> */}
      </div>
      {Object.keys(tabToIsPublishedStatusMap).find(key => Number(key) === currentTab) && (
        <Box
          sx={{
            padding: 3,
            paddingBottom: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 3,
            borderTop: `1px solid ${theme.palette.grey[200]}`,
            width: `calc(100% - ${theme.spacing(6)})`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <div>
            <Chip
              label={
                tabToIsPublishedStatusMap[currentTab]()
                  ? staticText.publish.currentlyPublished
                  : staticText.publish.unsavedChanges
              }
              type={tabToIsPublishedStatusMap[currentTab]() ? 'success' : 'status'}
              variant="outlined"
            />
          </div>
          <Divider orientation="vertical" variant="middle" flexItem />
          <div>
            <Button
              variant="outlined"
              color="secondary"
              disabled={tabToIsPublishedStatusMap[currentTab]()}
              onClick={() => {
                tabToResetActionMap[currentTab]();
              }}
            >
              {staticText.publish.resetButton}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              sx={{ ml: 1.5 }}
              disabled={tabToIsPublishedStatusMap[currentTab]()}
              onClick={() => {
                tabToPublishActionMap[currentTab]();
              }}
            >
              {staticText.publish.publishButton}
            </Button>
          </div>
        </Box>
    )}
    </Box>
  );
};

export default Configuration;

Configuration.propTypes = {
  actions: shape({
    deleteApplication: func.isRequired,
    fetchConnectConfigRequest: func.isRequired,
    hideSecret: func.isRequired,
    hideManagementToken: func.isRequired,
    initializeSelectedVehicleAccess: func.isRequired,
    regenerateManagementToken: func.isRequired,
    updateApplication: func.isRequired,
    updateConnectConfigRequest: func.isRequired,
  }).isRequired,
  connectConfig: shape({
    theme: string,
    logoUrl: string,
    supportedCountries: arrayOf(string),
    defaultRedirectUri: string,
    vehicleAccess: arrayOf(string),
  }).isRequired,
  match: shape({
    path: string.isRequired,
    params: shape({
      applicationId: string.isRequired,
    }),
  }).isRequired,
  ...applicationsProps,
  managementToken: string,
  selectedOrganization: string.isRequired,
  signals: objectOf(
    shape({
      signalsByGroup: arrayOf(
        shape({
          name: string.isRequired,
          description: string.isRequired,
          type: string.isRequired,
          permission: string.isRequired,
        }),
      ),
    }),
  ).isRequired,
};

Configuration.defaultProps = {
  managementToken: null,
};
